import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { MainLayout, PortfolioAllocation, TabsNavigationBlock, ContentHeadingBox } from 'src/components';
import { PAGES } from 'src/constants/pages';
import { getAllocationHook, useUserValues } from 'src/hooks';
import {
  currencySelector,
  investmentsFiltersSelector,
  preselectedAllocationSelector,
  selectedWorkspacesSelector
} from 'src/redux/selectors';
import { getInvestments, getInvestmentsFilters, getOutstandingBids } from 'src/redux/actions/investmentsActions';
import { getGrades, getPlatforms, onPresetAllocationId } from 'src/redux/actions/baseActions';
import { investmentsTypes } from 'src/redux/types';
import { GetInvestmentsLoansParams } from 'src/interfaces';
import { InvestmentContent } from './InvestmentContent';
import { pageTitleMap } from './constants';
import { InvestmentRequestParams } from './model';
import {
  getExportInvestmentsFileName,
  getExportInvestmentsUrl,
  getStatuses,
  GET_INVESTMENT_LOANS_DEFAULT_PARAMS
} from './InvestmentContent/helpers';
import { exportFileApi, getCurrentAllocation } from 'src/utils/comon';
import { ExportFileTypes } from 'src/constants/exportFilesConstants';
import styles from './InvestmentsPage.module.scss';

const investmentsTabs = [
  { label: 'Investments', path: PAGES.INVESTMENTS },
  { label: 'Outstanding Bids', path: PAGES.OUTSTANDING_BIDS }
];

const ALL_PORTFOLIOS_OPTION = { value: null, label: 'All Portfolios' };

const InvestmentsPage = (): JSX.Element => {
  const dispatch = useDispatch();

  const {
    location: { pathname }
  } = useHistory();

  const { allocations } = useSelector(investmentsFiltersSelector);
  const allocationIds = useMemo(() => (!isEmpty(allocations) ? allocations?.map(({ id }) => id) : []), [allocations]);
  const selectedWorkspace = useSelector(selectedWorkspacesSelector);

  const [loansReqParams, setLoansReqParams] = useState<InvestmentRequestParams>(null);

  const onAllocationChange = useCallback(
    (id: string) => {
      dispatch(onPresetAllocationId(id));
    },
    [dispatch]
  );

  const { selectedAllocation, selectorOptions, onChangeValue } = getAllocationHook({
    allocations,
    onAllocationChange,
    allDataOption: ALL_PORTFOLIOS_OPTION
  });

  const onTriggerClearFilters = useCallback(() => {
    onChangeValue(ALL_PORTFOLIOS_OPTION);
  }, [onChangeValue]);

  const isBidsPage = useMemo(() => pathname === PAGES.OUTSTANDING_BIDS, [pathname]);

  const allocationID = useSelector(preselectedAllocationSelector);
  const [isAllocationsFetching, setAllocationsFetching] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldResetFilters, setShouldResetFilters] = useState(false);
  const previousAllocationIdsRef = useRef<string[]>([]);

  // Set up loan request parameters when allocations change
  useEffect(() => {
    // Don't set params until allocations are fetched and we have selector options
    if (isAllocationsFetching || isEmpty(allocationIds) || isEmpty(selectorOptions)) {
      return;
    }

    const ids = allocationID ? [allocationID] : allocationIds;

    // Only update if this is a meaningful change - check against previous values
    const isAllocationChange = !isEqual(previousAllocationIdsRef.current, ids);

    if (isAllocationChange || !isInitialized) {
      setLoansReqParams((prev) => ({
        ...(prev ?? {}),
        ...GET_INVESTMENT_LOANS_DEFAULT_PARAMS(ids, getStatuses(isBidsPage), isBidsPage)
      }));

      // Update the ref to track current allocation IDs
      previousAllocationIdsRef.current = ids;
    }

    // Set the selected allocation in the dropdown only on initial load
    if (!isInitialized) {
      const preselectOption = selectorOptions.find((item) => item.value === allocationID) || ALL_PORTFOLIOS_OPTION;
      onChangeValue(preselectOption);
      setIsInitialized(true);
    }
  }, [allocationIds, allocationID, isBidsPage, selectorOptions, onChangeValue, isAllocationsFetching, isInitialized]);

  const onGetData = useCallback(
    (reqParams: GetInvestmentsLoansParams, onFinally: () => void) => {
      if (isBidsPage) {
        dispatch(getOutstandingBids(reqParams, onFinally, onFinally));
        return;
      }

      dispatch(getInvestments(reqParams, onFinally, onFinally));
    },
    [dispatch, isBidsPage]
  );

  const onFiltersReset = useCallback(() => {
    setShouldResetFilters(false);
  }, []);

  const { accessToken } = useUserValues();
  const currency = useSelector(currencySelector);

  const onConfirmExport = useCallback(
    async (fileType: ExportFileTypes, onDownloadedSuccess: () => void) => {
      const fileName = getExportInvestmentsFileName(isBidsPage);
      const fileUrl = getExportInvestmentsUrl(loansReqParams, currency.value, fileType, isBidsPage);

      await exportFileApi(fileUrl, fileName, fileType, accessToken);
      onDownloadedSuccess();
    },
    [accessToken, currency, isBidsPage, loansReqParams]
  );

  useEffect(() => {
    setAllocationsFetching(true);
    setLoansReqParams(null); // Reset params when workspace changes
    setIsInitialized(false); // Reset initialization state
    setShouldResetFilters(true); // Trigger filter reset in child component
    previousAllocationIdsRef.current = []; // Reset previous allocation tracking
    dispatch(getInvestmentsFilters(false, () => setAllocationsFetching(false)));
    dispatch(getGrades());
    dispatch(getPlatforms());

    return () => {
      dispatch({ type: investmentsTypes.CLEAR_INVESTMENTS });
      dispatch({ type: investmentsTypes.CLEAR_CACHED_REQ_PARAMS });
    };
  }, [dispatch, selectedWorkspace]);

  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    if (!loansReqParams || isAllocationsFetching) return;

    setLoading(true);
    onGetData({ ...loansReqParams, currency: currency.value }, () => setLoading(false));
  }, [loansReqParams, currency, onGetData, isAllocationsFetching]);

  return (
    <MainLayout isEmptySkeleton>
      <ContentHeadingBox wrapperClass={styles.controlPanel}>
        <TabsNavigationBlock
          tabs={investmentsTabs}
          wrapperClass={styles.tabsWrapper}
          tabLinkClass={styles.tabLinkClass}
        />

        <PortfolioAllocation
          selectedAllocation={getCurrentAllocation(selectorOptions, selectedAllocation)}
          selectorOptions={selectorOptions}
          onChangeValue={onChangeValue}
          isSearchable={false}
        />
      </ContentHeadingBox>

      <InvestmentContent
        allocationIds={allocationIds}
        isBidsPage={isBidsPage}
        pageTitle={pageTitleMap[pathname]}
        currency={currency.value}
        loansReqParams={loansReqParams}
        onSetLoansReqParams={setLoansReqParams}
        isLoading={isLoading}
        onSetLoading={setLoading}
        onConfirmExport={onConfirmExport}
        onTriggerClearFilters={onTriggerClearFilters}
        shouldResetFilters={shouldResetFilters}
        onFiltersReset={onFiltersReset}
      />
    </MainLayout>
  );
};

export default InvestmentsPage;
